import { useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { FeatureFlag } from '@fluenceur/types';
import { useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import { useResolveContactQuery } from '@/hooks/queries/use-resolve-contact-query';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useOpenContactDrawer } from '@/hooks/use-open-contact-drawer';
import { isBoostDevPreviewActive } from '@/libs/boostDevPreview';
import { buildContactShell } from './build-contact-shell';
import { CONTACT_DRAWER_QUERY_KEYS } from './contact-drawer-url-state';
import ContactDrawer from './ContactDrawer';
import { useContactDrawerError } from './use-contact-drawer-error';
import { useContactFeatureGuard } from './use-contact-feature-guard';
import { useContactResolveRef } from './use-contact-resolve-ref';
import { useLegacyContactParamRedirect } from './use-legacy-contact-param-redirect';
import { useResolvedContactIdentityId } from './use-resolved-contact-identity-id';

/**
 * Global mount point for the unified contact drawer. Mounted once in Layout.
 * Presence of the `contactId` URL param opens it. This is the only contact
 * surface — the legacy affiliate/creator drawers are gone; old bookmarked URLs
 * are translated onto these params by `useLegacyContactParamRedirect`.
 *
 * The header (name / email / avatar) and the collaboration rail rows are built
 * from the real creator identity resolved from `contactId` + `contactEntry`.
 * Every view body fetches its own live slice and renders an explicit empty state
 * when the identity has no data yet — there is no mock fallback. While the
 * resolve query is loading the header is empty and fills in on settle; a settled
 * resolve/summary failure renders the drawer's explicit error state instead
 * (see `useContactDrawerError` for the unsaved-creator exception).
 */
const GlobalContactDrawer: React.FC = () => {
  const { t, i18n } = useTranslation();
  useLegacyContactParamRedirect();
  const [contactId] = useQueryState(CONTACT_DRAWER_QUERY_KEYS.contactId);
  const resolveRef = useContactResolveRef();
  const { user } = useAuth();
  const { data: resolved } = useResolveContactQuery(resolveRef);
  const identityId = useResolvedContactIdentityId();
  const { data: summary } = useContactSummaryQuery(user?.currentBrandId, identityId);
  const { isError, retry } = useContactDrawerError();
  const { close } = useOpenContactDrawer();

  // Gate the two feature-flagged rail rows so an org without the feature never
  // sees an affordance the backend would reject. Mirrors the boost gate used on
  // the nav (flag OR the DEV-only boost preview) for the ads row.
  const { collabsEnabled, canRenderContactView } = useContactFeatureGuard();
  const partnershipAdsEnabled =
    useFeatureFlag(FeatureFlag.PARTNERSHIP_ADS_BOOST) || isBoostDevPreviewActive();

  // A stale URL may carry a contactEntry outside CONTACT_ENTRIES (e.g. from an
  // older release), which parseAsStringLiteral maps to null, leaving
  // resolveRef=null. The drawer would mount as an empty shell with no query
  // ever firing. Purge all contact params so the URL self-heals without any
  // visible error state.
  useEffect(() => {
    if (contactId && !resolveRef) {
      close();
    }
  }, [contactId, resolveRef, close]);

  if (!contactId) {
    return null;
  }

  // Do not render while the URL is being self-healed by the effect above.
  if (!resolveRef) {
    return null;
  }

  // Wait for the feature query before restoring a gated deep-link. Denied
  // Collabs URLs are reset to the profile view by useContactFeatureGuard.
  if (!canRenderContactView) {
    return null;
  }

  const contact = buildContactShell(
    resolved,
    summary,
    t,
    i18n.language,
    collabsEnabled,
    partnershipAdsEnabled
  );

  return <ContactDrawer contact={contact} onClose={close} error={isError ? { retry } : null} />;
};

export default GlobalContactDrawer;
